import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { clsb, clsx } from 'js/utils/classNames.util';
import { componentService } from 'js/services/component.service';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from './helpers';
import { withFinalFormField } from '../../hocs';
import { getInteractionClass, preventDefault } from '../../helpers';
import { FieldBottomMessage } from '../FieldBottomMessage';

import '../../FormFields.scss';
import style from './Select.scss';

export const SelectBare: React.FC<Types.ISelectProps> = ({
    id,
    meta,
    hint,
    label,
    onChange,
    readOnly,
    disabled,
    leftIcon,
    rightIcon,
    className,
    classNames,
    infoTooltip,
    onMouseDown,
    options = [],
    currencySymbol,
    placeholder = '',
    'aria-label': ariaLabel,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    ...props
}) => {
    const idRef = useRef<string>(id || uuid());
    const isDisabled = meta?.submitting || meta?.validating || disabled;
    const translate = useTranslations();
    const selectRef = useRef<HTMLSelectElement>(null);
    const selectOptions = useMemo(() => helpers.parseOptions(options), [options]);
    const interactionClass = getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });
    const propsValue = props.value || props.defaultValue;
    // needed to change placeholder option color
    const emptySelectClassName = props.value !== null && props.value !== '' && props.defaultValue !== '' ? '' : style.empty;

    // this section is needed to use select as replacer
    // if not needed could be replaced with const displayValue = helpers.getSelectLabel(options, String(props.value), placeholder)
    const [displayValue, setDisplayValue] = useState(helpers.getSelectLabel(selectOptions, String(propsValue), placeholder));

    const onSelectChange = useCallback((event: React.FocusEvent<HTMLSelectElement>): void => {
        onChange && onChange(event);

        setDisplayValue(helpers.getSelectLabel(selectOptions, String(event.target.value), placeholder));
    }, [onChange, selectOptions, placeholder]);

    useEffect(() => {
        setDisplayValue(helpers.getSelectLabel(selectOptions, String(propsValue), placeholder));
    }, [selectOptions, placeholder, propsValue]);

    return (
        <div className={clsx([clsb([style, classNames], ['container']), emptySelectClassName, interactionClass, className])}>
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    label={label}
                    id={idRef.current}
                    readOnly={readOnly}
                    classNames={classNames}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                />
            )}

            <div className={clsb([style, classNames], ['innerContainer'])}>
                {leftIcon && leftIcon()}

                {currencySymbol && <span className={clsb([style, classNames], ['currencySymbol'])}>{currencySymbol}</span>}

                <select
                    ref={selectRef}
                    id={idRef.current}
                    disabled={isDisabled}
                    onChange={onSelectChange}
                    tabIndex={isDisabled || readOnly ? -1 : 0}
                    className={clsb([style, classNames], ['select'])}
                    onMouseDown={readOnly ? preventDefault : onMouseDown}
                    aria-label={ariaLabel || translate('playtech.mobile.system.select.aria-label')}
                    {...props}
                    {...props.value === undefined ? { defaultValue: props.defaultValue || '' } : {}}
                >
                    {(placeholder || isPlaceholderAsteriskEnabled) && (
                        <option
                            value=""
                            disabled
                            className={clsb([style, classNames], ['placeholder'])}
                        >
                            {`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                        </option>
                    )}
                    {selectOptions.map(item => (
                        <option
                            value={item.value}
                            key={item.key || item.value}
                            className={classNames?.option}
                        >
                            {translate(item.label)}
                        </option>
                    ))}
                </select>

                <div className={clsb([style, classNames], ['selectDummy'])}>{displayValue && translate(displayValue)}</div>

                {rightIcon && rightIcon()}

                <Icon
                    type="arrowDropdown"
                    disabled={isDisabled || readOnly}
                    className={clsb([style, classNames], ['dropDownIcon'])}
                />
            </div>

            <FieldBottomMessage hint={hint} meta={meta} classNames={classNames} />
        </div>
    );
};

export const SelectPure = memo(SelectBare);
export const Select = withFinalFormField<string>()(SelectPure);

componentService.registerWebContentComponents({ Select: SelectPure });
